import { datadogRum } from '@datadog/browser-rum';
import { addReactError, reactPlugin } from '@datadog/browser-rum-react';

import packageJson from '@/../package.json';

import type {
  ErrorContext,
  InstrumentationProvider,
  LogLevel,
  ProviderConfig,
  ReactErrorHandler,
  UserInfo,
} from '../types';

/**
 * DataDog RUM configuration from environment variables
 * These should be set in .env files with VITE_APP_ prefix
 */
interface DataDogConfig {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
}

/**
 * Get DataDog configuration from environment variables
 */
function getDataDogConfig(): DataDogConfig {
  const applicationId = 'b9a08cfe-81d6-4cff-903b-dab4ba15a3bb';
  const clientToken = 'pube16bf7701a4eb4e71216b7ae8728b238';
  const site = 'datadoghq.com';
  const service = 'student-app';
  return { applicationId, clientToken, site, service };
}

/**
 * DataDog RUM instrumentation provider for real user monitoring
 */
export class DataDogProvider implements InstrumentationProvider {
  name = 'datadog';
  private initialized = false;

  init(config: ProviderConfig): void {
    if (!config.enabled || this.initialized) {
      return;
    }

    const ddConfig = getDataDogConfig();

    datadogRum.init({
      applicationId: ddConfig.applicationId,
      clientToken: ddConfig.clientToken,
      site: ddConfig.site,
      service: ddConfig.service,
      env: config.environment,
      version: config.release ?? packageJson.version,

      // Session sampling
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20, // 20% of sessions will have replay

      // Tracking options
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,

      // Privacy settings
      defaultPrivacyLevel: 'mask-user-input',

      // React integration plugin with router support
      plugins: [reactPlugin({ router: true })],

      // Filter out sensitive data and noisy errors
      beforeSend: event => {
        // Filter out browser extension errors
        const isExtensionError =
          event.type === 'error' && event.error?.stack?.includes('extensions/');

        // Filter out network errors we can't control
        const isNetworkError =
          event.type === 'error' &&
          event.error?.message?.includes('Network request failed');

        return !isExtensionError && !isNetworkError;
      },
    });

    this.initialized = true;
  }

  captureException(error: Error, context?: ErrorContext): void {
    if (!this.initialized) return;

    datadogRum.addError(error, {
      ...context?.tags,
      ...(context?.errorInfo
        ? { errorInfo: JSON.stringify(context.errorInfo) }
        : {}),
    });
  }

  captureMessage(message: string, level: LogLevel = 'info'): void {
    if (!this.initialized) return;

    // DataDog RUM uses custom actions for messages
    datadogRum.addAction(message, {
      level,
      type: 'custom_message',
    });
  }

  setUser(user: UserInfo | null): void {
    if (!this.initialized) return;

    if (user) {
      datadogRum.setUser({
        id: user.id,
        email: user.email,
        name: user.name,
      });
    } else {
      datadogRum.clearUser();
    }
  }

  setContext(key: string, value: Record<string, unknown>): void {
    if (!this.initialized) return;

    datadogRum.setGlobalContextProperty(key, value);
  }

  addBreadcrumb(breadcrumb: {
    category: string;
    message: string;
    level?: LogLevel;
    data?: Record<string, unknown>;
  }): void {
    if (!this.initialized) return;

    // DataDog RUM uses actions instead of breadcrumbs
    datadogRum.addAction(breadcrumb.message, {
      category: breadcrumb.category,
      level: breadcrumb.level,
      ...breadcrumb.data,
    });
  }

  getReactErrorHandler(): ReactErrorHandler {
    if (!this.initialized) {
      return () => {
        // No-op when not initialized
      };
    }

    return (error, errorInfo) => {
      addReactError(error instanceof Error ? error : new Error(String(error)), {
        componentStack: errorInfo.componentStack,
      });
    };
  }
}

export const datadogProvider = new DataDogProvider();
